import { Link, useParams } from 'react-router';
import {
  isOrgCapabilityLive,
  usableOrgCapabilities,
  type Organization,
  type OrgCapability,
} from '@gatecheckpro/contracts';
import {
  Card,
  EmptyState,
  LoadingRegion,
  Status,
  presentation,
  VERIFICATION_STATES,
} from '@gatecheckpro/design-system';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useViewer } from '../lib/session.tsx';

export function OverviewPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const viewer = useViewer();
  const { resource } = useResource<Organization>(
    organizationId === undefined ? null : `/api/v1/organizer/organizations/${organizationId}`,
  );

  if (resource.status === 'loading') return <LoadingRegion label="Loading organization" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  const organization = resource.data;
  const verification = presentation(VERIFICATION_STATES, organization.verification.state);

  /**
   * What this viewer can actually do here, by the same two filters the
   * workspace navigation applies: the capability must be built, and this
   * session must be allowed to exercise it. Pointing someone at a page their
   * role cannot open would be the product lie those filters exist to prevent.
   */
  const capabilities = usableOrgCapabilities(
    (viewer.organizations.find((candidate) => candidate.organizationId === organizationId)
      ?.capabilities ?? []) as OrgCapability[],
    { mfaSatisfied: viewer.mfa.satisfiedForSession },
  ).filter(isOrgCapabilityLive);

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-grid">
        <Card title="Verification" eyebrow="CABS onboarding">
          <div className="gc-stack">
            <Status
              tone={verification.tone}
              glyph={verification.glyph}
              label={verification.label}
              title={verification.meaning}
            />
            <p className="gc-muted">{verification.meaning}</p>
            {organization.verification.reasonCode !== null && (
              <p className="gc-muted">Reason: {organization.verification.reasonCode}</p>
            )}
            <Link to="verification">Open verification</Link>
          </div>
        </Card>

        <Card title="Your access" eyebrow="This organization">
          <div className="gc-stack gc-stack--tight">
            <p className="gc-muted">Signed in as {viewer.displayName}</p>
            <p>
              {viewer.organizations
                .find((candidate) => candidate.organizationId === organizationId)
                ?.roles.join(' · ') ?? 'No roles'}
            </p>
            {!viewer.mfa.enrolled && (
              <p className="gc-muted">
                Some actions need a second factor. <Link to="/security">Set one up</Link>.
              </p>
            )}
          </div>
        </Card>

        <Card title="Contact" eyebrow="Profile">
          <div className="gc-stack gc-stack--tight">
            <p>{organization.profile.publicName}</p>
            <p className="gc-muted">{organization.contactEmail}</p>
            <p className="gc-muted">{organization.countryCode}</p>
          </div>
        </Card>
      </div>

      {/*
        This block was an empty state reading "Events arrive in the next slice",
        and it was still saying so at Slice 9B — long after events, venues,
        gates, zones and ticket products had all shipped. It was the landing
        page of the workspace telling every organizer that the product could not
        do the thing they had just been given a menu item for.

        What replaces it is derived rather than asserted: either the viewer can
        configure events, in which case say where, or they cannot, in which case
        say that instead of describing a release.
      */}
      {capabilities.includes('EVENT_MANAGE') ? (
        <Card title="Where to start" eyebrow="Setting up a show">
          <div className="gc-stack gc-stack--tight">
            <p className="gc-muted">
              An event carries its sessions, its ticket products and the capacity that goes on sale.
              A venue carries the zones those tickets admit to and the gates people come through.
            </p>
            <div className="gc-row">
              <Link to="events">Events</Link>
              <Link to="venues">Venues</Link>
            </div>
          </div>
        </Card>
      ) : (
        <EmptyState title="Your role does not configure events">
          Setting up events, venues and tickets needs a capability your role in this organization
          does not include. Nothing you do hold is hidden — this organization, its team, its
          verification and its audit trail are all here and all real.
        </EmptyState>
      )}
    </div>
  );
}
